import { styled } from "@mui/material";
import { Suspense } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { MapContainer, MapContainerProps, ZoomControl } from "react-leaflet";
import { useMapPosition } from "../../hooks/mapHooks";

import { Settings } from "../../types";

import { BackgroundLayer } from "./BackgroundLayer";
import { MapSizeObserver } from "./MapSizeObserver";
import { MaskLayers } from "./MaskLayers";
import { PersistMapPosition } from "./PersistMapPosition";
import { SearchedLayer } from "./SearchedLayer";
import { ShareControl } from "./ShareControl";

interface Props extends MapContainerProps {
  settings: Settings;
}

export function MapContent(props: Props) {
  const { settings, ...mapProps } = props;
  const mapPosition = useMapPosition(settings.initialPosition);

  return (
    <Styles
      zoomControl={false}
      scrollWheelZoom={true}
      center={[mapPosition.lat, mapPosition.lng]}
      zoom={mapPosition.zoom}
      {...mapProps}
    >
      {mapProps.children}
      <ShareControl position="topleft" />
      <PersistMapPosition />
      <MapSizeObserver />
      <ErrorBoundary fallbackRender={() => null}>
        <Suspense fallback={null}>
          <SearchedLayer />
          <MaskLayers />
        </Suspense>
      </ErrorBoundary>
      <BackgroundLayer backgroundIndicators={settings.backgroundIndicators} />
      <ZoomControl position="bottomright" />
    </Styles>
  );
}

const Styles = styled(MapContainer)<MapContainerProps>`
  .leaflet-control-zoom {
    border: unset;
    display: flex;
    flex-direction: column;

    a[role="button"] + a[role="button"] {
      margin-top: ${({ theme }) => theme.spacing(1)};
    }

    a[role="button"] {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      outline: 0;
      border: 0;
      cursor: pointer;
      user-select: none;
      vertical-align: middle;
      text-decoration: none;
      font-weight: 700;
      font-size: 1rem;
      transition: background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
        box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
        border-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
      border-radius: 50%;
      width: ${({ theme }) => theme.spacing(5)};
      height: ${({ theme }) => theme.spacing(5)};
      box-shadow: 0px 3px 5px -1px rgb(0 0 0 / 20%),
        0px 6px 10px 0px rgb(0 0 0 / 14%), 0px 1px 18px 0px rgb(0 0 0 / 12%);
      color: rgba(0, 0, 0, 0.87);
      background-color: ${({ theme }) => theme.palette.grey[300]}; /* #e0e0e0;*/

      &:hover {
        background-color: ${({ theme }) => theme.palette.grey[100]};
      }
    }
  }
`;
